// filepath: apps/web/src/features/cabinet/styles.ts
//
// FR-UI-CABINET-1 — shared inline-style constants for the tenant
// workspace shell.
//
// The SPA build has no CSS pipeline / Tailwind / styled-components
// today (see `.github/instructions/react-conventions.instructions.md`
// §Styling: "every visual property is an inline `style={{}}`"). The
// established working precedent (TaskInboxPage, DrilldownLink,
// OperatorShell chrome) is concrete inline values rather than
// `var(--…)` custom properties, because the design-system token set
// (`--surface-page`, `--space-8`, …) is not yet wired into a `:root`
// declaration anywhere in the build (no `.css` file exists under
// `apps/web/`). Until that token-infra task lands, concrete values
// are the only way the shell renders with usable layout.
//
// The values below are PORTED VERBATIM from the canonical design-
// system palette at `docs/guides/design system/tokens/colors.css`
// (blue `#3b82f6` primary; emerald / red / amber semantic; cool-gray
// neutrals; 4px control radius). They are NOT invented — they mirror
// the token values the design system owns, just inlined because the
// token→`:root` bridge is not built yet. When the token bridge lands,
// every value here can be replaced by its `var(--…)` alias in one
// place (this file), leaving the component source untouched.
//
// SECURITY: no tenant-authored values flow through these constants.
// They are platform chrome only (INV-9 — rendered as text/style, no
// `dangerouslySetInnerHTML`).

import type React from "react";

/**
 * Cool-gray neutral palette (design-system `colors.css` cool-gray
 * ramp). Used for surfaces, borders, and text across the shell.
 */
export const NEUTRAL = {
  /** Primary text — cool-gray-900. */
  textPrimary: "#111827",
  /** Secondary / body text — cool-gray-600. */
  textSecondary: "#4b5563",
  /** Tertiary / muted text — cool-gray-500. */
  textTertiary: "#6b7280",
  /** Default border — cool-gray-200. */
  borderDefault: "#e5e7eb",
  /** Card / elevated surface — white. */
  surfaceCard: "#ffffff",
  /** Page background — cool-gray-50. */
  surfacePage: "#f9fafb",
  /** Sidebar background — cool-gray-900 (dark rail). */
  surfaceSidebar: "#111827",
  /** Sidebar nav text — cool-gray-300. */
  sidebarText: "#d1d5db",
  /** Sidebar nav active accent — white. */
  sidebarActiveText: "#ffffff",
  /** Sidebar section label — cool-gray-500. */
  sidebarSectionLabel: "#9ca3af",
} as const;

/**
 * Semantic palette (design-system `colors.css` semantic aliases).
 */
export const SEMANTIC = {
  /** Primary action / active link — blue-500. */
  primary: "#3b82f6",
  /** Primary-pressed — blue-700. */
  primaryPressed: "#1d4ed8",
  /** Danger / failed — red-500. */
  danger: "#ef4444",
} as const;

/**
 * Spacing scale (design-system 4px base unit). Inlined because the
 * `--space-*` tokens are not yet wired into `:root`.
 */
export const SPACE = {
  2: "8px",
  3: "12px",
  4: "16px",
  6: "24px",
  8: "32px",
} as const;

/** Control radius — references the design-system radius token. */
export const RADIUS_MD = "var(--radius-sm)";

/** Full / pill radius — references the design-system radius token. */
export const RADIUS_FULL = "var(--radius-full)";

/**
 * Sidebar rail width. Fixed so the content slot has a stable left
 * margin across section switches (AC-1 sidebar persistence).
 */
export const SIDEBAR_WIDTH = "240px";

/**
 * The standard visually-hidden element style (WCAG 2.1 sr-only
 * technique). Used for the shell's landmark heading (FR-UI-CORE-9)
 * so screen readers announce the workspace label without displaying
 * redundant visible text.
 */
export const VISUALLY_HIDDEN: React.CSSProperties = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: "0",
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: "0",
} as const;
